import { Keyboard } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { AfricanKeyboard } from '@/components/keyboard';
import { TaskItemCard, fillCopy } from '@/features/contribution-kit';
import type { TranscriptionPhraseCopy } from './transcription-phrase-copy';

type TranscriptionPhraseWriteCardProps = {
  t: Record<string, unknown>;
  copy: TranscriptionPhraseCopy;
  translatedText: string;
  onTranslatedTextChange: (value: string) => void;
  showKeyboard: boolean;
  onToggleKeyboard: () => void;
  selectedLanguage: string;
  targetLanguageLabel: string;
  isLoading: boolean;
  hasPhrases: boolean;
};

/** What the person writes: the translation, with the African keyboard on demand. */
export function TranscriptionPhraseWriteCard({
  t,
  copy,
  translatedText,
  onTranslatedTextChange,
  showKeyboard,
  onToggleKeyboard,
  selectedLanguage,
  targetLanguageLabel,
  isLoading,
  hasPhrases,
}: TranscriptionPhraseWriteCardProps) {
  return (
    <TaskItemCard
      label={copy.writeLabel}
      corner={<span className="text-xs text-muted-foreground">{fillCopy(copy.intoLanguage, { language: targetLanguageLabel })}</span>}
    >
      <Textarea
        value={translatedText}
        aria-label={copy.writeLabel}
        onChange={(e) => onTranslatedTextChange(e.target.value)}
        placeholder={String(t.writeTranslationHere ?? '')}
        disabled={isLoading || !hasPhrases}
        className="min-h-[140px] bg-surface text-base"
      />

      <button
        type="button"
        onClick={onToggleKeyboard}
        aria-pressed={showKeyboard}
        className="inline-flex h-11 items-center gap-2 rounded-md px-2 text-sm text-muted-foreground transition-colors hover:bg-surface-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Keyboard className="size-4" strokeWidth={1.6} aria-hidden="true" />
        <span>{String(t.africanKeyboard ?? '')}</span>
        <span className="sr-only">{showKeyboard ? copy.keyboardHide : copy.keyboardShow}</span>
        <span className={`size-1.5 rounded-full ${showKeyboard ? 'bg-primary' : 'bg-border'}`} aria-hidden="true" />
      </button>

      {showKeyboard ? (
        <div className="border-t border-border/60 pt-4">
          <AfricanKeyboard language={selectedLanguage} value={translatedText} onChange={onTranslatedTextChange} onClose={onToggleKeyboard} />
        </div>
      ) : null}
    </TaskItemCard>
  );
}
